import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import api from '@/lib/api';
import { SpinnerGap, Star, UserSwitch, Percent, Buildings, Megaphone, Trash, PencilSimple, Plus, X, Receipt, MagnifyingGlass, FileArrowDown, CaretDown, CaretRight, XCircle, EnvelopeSimple, CheckCircle, FloppyDisk } from '@phosphor-icons/react';
import TabCarregamentos from './backoffice/TabCarregamentos';
import TabFacebookLeads from './backoffice/TabFacebookLeads';
import TabWebhookRejeicoes from './backoffice/TabWebhookRejeicoes';

const tabs = [
  { id: 'geral', label: 'Geral' },
  { id: 'encomendas', label: 'Encomendas' },
  { id: 'consultores', label: 'Consultores' },
  { id: 'empresas', label: 'Empresas' },
  { id: 'campanhas', label: 'Campanhas' },
  { id: 'subscricao', label: 'Subscrição Leads' },
  { id: 'carregamentos', label: 'Carregamentos' },
  { id: 'fbleads', label: 'Importar FB Leads' },
  { id: 'webhookrej', label: 'Webhooks Rejeitados' },
  { id: 'whatsapp', label: 'WhatsApp Templates' },
];

/* ── Shared Components ── */
function ActionCard({ title, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm" data-testid={testid}>
      <h3 className="text-base font-semibold text-[#111827] mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex-1 w-full">
      <label className="block text-xs font-medium text-[#6B7280] mb-1">{label}</label>
      {children}
    </div>
  );
}

function ActionBtn({ onClick, disabled, loading, children, testid, variant = 'primary' }) {
  const cls = variant === 'primary'
    ? 'bg-[#0F1829] hover:bg-[#001f6e] text-white'
    : 'bg-green-600 hover:bg-green-700 text-white';
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled || loading}
      className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-md disabled:opacity-40 transition-colors whitespace-nowrap ${cls}`}>
      {loading && <SpinnerGap size={16} className="animate-spin" />}
      {children}
    </button>
  );
}

function ResultMsg({ result }) {
  if (!result) return null;
  const ok = result.success;
  return (
    <div data-testid="action-result" className={`mt-3 px-4 py-2.5 rounded-md border text-sm font-medium ${
      ok ? 'bg-green-50 border-green-300 text-green-700' : 'bg-red-50 border-red-300 text-red-700'
    }`}>{result.message}</div>
  );
}

const inputCls = "w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none";
const selectCls = `${inputCls} bg-white`;

/* ══════════════════════════════════════════════ */
/*  Tab Geral                                      */
/* ══════════════════════════════════════════════ */
function TabGeral() {
  return (
    <div className="space-y-6">
      <MudarTipoConta />
      <GerarFaturaRecibo />
      <StarsMesAnterior />
    </div>
  );
}

function GerarFaturaRecibo() {
  const [idEncomenda, setIdEncomenda] = useState('');
  const [tipo, setTipo] = useState('FE');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!idEncomenda.trim() || !tipo) {
      setResult({ success: false, message: 'ID de Encomenda e Tipo de Emissão são obrigatórios.' });
      return;
    }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post('/backoffice/emitirDocumento', {
        id_encomenda: idEncomenda.trim(),
        tipo_emissao: tipo,
      });
      setResult(data);
      if (data?.success) { setIdEncomenda(''); setTipo('FE'); }
    } catch {
      setResult({ success: false, message: 'Erro de comunicação.' });
    } finally { setLoading(false); }
  };

  return (
    <ActionCard title="Gerar Fatura/Recibo/Notas de Crédito" testid="section-gerar-fatura">
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <Field label="ID Encomenda">
          <input data-testid="bo-fatura-encomenda" type="text" value={idEncomenda}
            onChange={e => setIdEncomenda(e.target.value)}
            placeholder="ID Encomenda para gerar fatura" className={inputCls} />
        </Field>
        <Field label="Tipo de Emissão">
          <select data-testid="bo-fatura-tipo" value={tipo} onChange={e => setTipo(e.target.value)} className={selectCls}>
            <option value="FE">Fatura para cliente empresarial</option>
            <option value="FT">Emitir Fatura e Recibo</option>
            <option value="RB">Emitir Recibo</option>
            <option value="NC">Emitir Nota de Crédito</option>
          </select>
        </Field>
        <ActionBtn onClick={submit} disabled={!idEncomenda.trim()} loading={loading} testid="bo-fatura-btn">
          Criar Documento
        </ActionBtn>
      </div>
      <ResultMsg result={result} />
    </ActionCard>
  );
}

function MudarTipoConta() {
  const [idCliente, setIdCliente] = useState('');
  const [tipo, setTipo] = useState('');
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/backoffice/clientTypes').then(({ data }) => {
      if (data.success) setTypes(data.data || []);
    }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!idCliente || !tipo) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post('/backoffice/changeAccountType', { id_cliente: idCliente, tipo_cliente: tipo });
      setResult(data);
      if (data.success) { setIdCliente(''); setTipo(''); }
    } catch { setResult({ success: false, message: 'Erro de comunicação.' }); }
    finally { setLoading(false); }
  };

  return (
    <ActionCard title="Mudar Tipo de Conta" testid="section-mudar-tipo-conta">
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <Field label="ID Cliente">
          <input data-testid="bo-tipo-id" type="text" value={idCliente} onChange={e => setIdCliente(e.target.value)}
            placeholder="ID do Cliente" className={inputCls} />
        </Field>
        <Field label="Novo Tipo">
          <select data-testid="bo-tipo-select" value={tipo} onChange={e => setTipo(e.target.value)} className={selectCls}>
            <option value="">Selecione tipo...</option>
            {types.map(t => <option key={t.ID_TipoCliente} value={t.ID_TipoCliente}>{t.Descricao} ({t.ID_TipoCliente})</option>)}
          </select>
        </Field>
        <ActionBtn onClick={submit} disabled={!idCliente || !tipo} loading={loading} testid="bo-tipo-btn">
          Alterar
        </ActionBtn>
      </div>
      <ResultMsg result={result} />
    </ActionCard>
  );
}

function StarsMesAnterior() {
  const [stars, setStars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/backoffice/getStars', {});
      if (data.success) setStars(data.data || []);
      setLoaded(true);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <ActionCard title="Stars Mês Anterior" testid="section-stars">
      <div className="flex items-end gap-3 mb-4">
        <ActionBtn onClick={load} loading={loading} testid="bo-stars-btn">
          <Star size={16} weight="fill" />
          Obter Stars
        </ActionBtn>
        {loaded && <span className="text-xs text-[#9CA3AF]">{stars.length} resultado(s)</span>}
      </div>
      {loaded && stars.length > 0 && (
        <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="bo-stars-table">
            <thead>
              <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-3 py-2 text-left font-medium">Nome Consultor</th>
                <th className="px-3 py-2 text-center font-medium">Stars</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {stars.map(s => (
                <tr key={s.ID_Cliente} className="hover:bg-[#F9FAFB]">
                  <td className="px-3 py-2 font-medium text-[#111827]">{s.NomeCompleto}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold">
                      <Star size={12} weight="fill" /> {s.Reconhecimento}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {loaded && stars.length === 0 && <p className="text-sm text-[#9CA3AF]">Sem dados de stars.</p>}
    </ActionCard>
  );
}

/* ══════════════════════════════════════════════ */
/*  Tab Encomendas                                 */
/* ══════════════════════════════════════════════ */
const ESTADO_COLORS = {
  AP: { bg: '#FEF3C7', text: '#92400E', label: 'Aguarda Pagamento' },
  PA: { bg: '#D1FAE5', text: '#065F46', label: 'Pago' },
  PR: { bg: '#D1FAE5', text: '#065F46', label: 'Em Processamento' },
  EC: { bg: '#D1FAE5', text: '#065F46', label: 'Expedido' },
  DO: { bg: '#DBEAFE', text: '#1E40AF', label: 'Digital OK' },
  EV: { bg: '#DBEAFE', text: '#1E40AF', label: 'Evento' },
  FE: { bg: '#E0F2FE', text: '#075985', label: 'Finalizada' },
  CC: { bg: '#FEE2E2', text: '#991B1B', label: 'Cancelada Cliente' },
  PC: { bg: '#FEE2E2', text: '#991B1B', label: 'Pagamento Cancelado' },
};
const ESTADOS_CONCLUIDOS = ['PR', 'EC', 'DO', 'EV', 'FE'];
const fmtEUR = v => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const fmtDate = d => d ? new Date(d).toLocaleDateString('pt-PT') : '-';

function TabEncomendas() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchId, setSearchId] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async (overrides = {}) => {
    setLoading(true); setError(null);
    const p = overrides.page != null ? overrides.page : page;
    const ps = overrides.pageSize != null ? overrides.pageSize : pageSize;
    const sid = overrides.searchId != null ? overrides.searchId : searchId;
    try {
      const { data } = await api.post('/backoffice/orders/list', {
        pagina: p,
        resultados_por_pagina: ps,
        campo_ordenacao: 'ID_Encomenda',
        campo_ordenacao_asc: false,
        id_encomenda: sid ? Number(sid) : null,
      });
      if (data?.success) {
        setOrders(data.obj?.dados || []);
        setTotal(data.obj?.total || 0);
      } else {
        setOrders([]); setTotal(0);
        setError(data?.message || 'Não foi possível carregar as encomendas.');
      }
    } catch {
      setError('Erro de comunicação com o servidor legacy.');
      setOrders([]); setTotal(0);
    } finally { setLoading(false); }
  }, [page, pageSize, searchId]);

  useEffect(() => {
    const t = setTimeout(() => load({ page: 0 }), searchId ? 500 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Client-side state filter (API legacy não suporta filtro por estado)
  const uniqueEstados = Array.from(new Map(orders.map(o => [o.ID_EstadoEncomenda, o.EstadoEncomenda || o.ID_EstadoEncomenda])).entries())
    .sort((a, b) => a[1].localeCompare(b[1], 'pt'));
  const filteredOrders = estadoFilter ? orders.filter(o => o.ID_EstadoEncomenda === estadoFilter) : orders;

  return (
    <div className="space-y-4" data-testid="tab-encomendas">
      <ActionCard title="Pesquisar Encomendas" testid="section-encomendas-filter">
        <div className="flex flex-col sm:flex-row gap-3 items-end flex-wrap">
          <Field label="ID Encomenda">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input data-testid="bo-orders-search" type="number" value={searchId}
                onChange={e => setSearchId(e.target.value)}
                placeholder="Filtrar por ID..." className={`${inputCls} pl-8`} />
            </div>
          </Field>
          <Field label="Estado">
            <select data-testid="bo-orders-estado" value={estadoFilter}
              onChange={e => setEstadoFilter(e.target.value)} className={selectCls}>
              <option value="">Todos os estados</option>
              {uniqueEstados.map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
          </Field>
          <Field label="Por página">
            <select data-testid="bo-orders-pagesize" value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
              className={selectCls}>
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <ActionBtn onClick={() => load({ page: 0 })} loading={loading} testid="bo-orders-refresh">
            <Receipt size={16} /> Atualizar
          </ActionBtn>
          {estadoFilter && (
            <span className="text-xs text-[#6B7280] pb-2">
              A mostrar {filteredOrders.length} de {orders.length} nesta página ·{' '}
              <button onClick={() => setEstadoFilter('')} className="text-[#0F1829] hover:underline">limpar</button>
            </span>
          )}
        </div>
        {error && <ResultMsg result={{ success: false, message: error }} />}
      </ActionCard>

      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bo-orders-table">
            <thead>
              <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-3 py-2.5 text-left font-medium w-8"></th>
                <th className="px-3 py-2.5 text-left font-medium">ID</th>
                <th className="px-3 py-2.5 text-left font-medium">Cliente</th>
                <th className="px-3 py-2.5 text-left font-medium">Estado</th>
                <th className="px-3 py-2.5 text-left font-medium">Data</th>
                <th className="px-3 py-2.5 text-right font-medium">Preço Final</th>
                <th className="px-3 py-2.5 text-center font-medium" title="Fatura">F</th>
                <th className="px-3 py-2.5 text-center font-medium" title="Recibo">R</th>
                <th className="px-3 py-2.5 text-center font-medium" title="Nota de Crédito">NC</th>
                <th className="px-3 py-2.5 text-center font-medium" title="Pagamento">P</th>
                <th className="px-3 py-2.5 text-center font-medium" title="Cancelar">C</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {loading && filteredOrders.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-[#9CA3AF]">
                  <SpinnerGap size={24} className="animate-spin inline mr-2" /> A carregar encomendas...
                </td></tr>
              )}
              {!loading && filteredOrders.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-[#9CA3AF]">
                  {estadoFilter ? 'Sem encomendas com esse estado nesta página.' : 'Sem encomendas.'}
                </td></tr>
              )}
              {filteredOrders.map(o => (
                <OrderRow key={o.ID_Encomenda} order={o}
                  expanded={expandedId === o.ID_Encomenda}
                  onToggle={() => setExpandedId(expandedId === o.ID_Encomenda ? null : o.ID_Encomenda)}
                  onChanged={() => load()} />
              ))}
            </tbody>
          </table>
        </div>

        {total > pageSize && (
          <div className="px-4 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB] flex items-center justify-between text-xs">
            <span className="text-[#6B7280]">Total: <strong>{total}</strong> encomendas</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading}
                data-testid="bo-orders-prev"
                className="px-3 py-1.5 border border-[#D1D5DB] rounded-md hover:bg-white disabled:opacity-40">Anterior</button>
              <span className="text-[#374151]">Página <strong>{page + 1}</strong> de <strong>{totalPages}</strong></span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page + 1 >= totalPages || loading}
                data-testid="bo-orders-next"
                className="px-3 py-1.5 border border-[#D1D5DB] rounded-md hover:bg-white disabled:opacity-40">Próxima</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderRow({ order: o, expanded, onToggle, onChanged }) {
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const estado = ESTADO_COLORS[o.ID_EstadoEncomenda] || { bg: '#F3F4F6', text: '#374151', label: o.EstadoEncomenda || o.ID_EstadoEncomenda };

  useEffect(() => {
    if (expanded && !details) {
      setLoadingDetails(true);
      api.post('/backoffice/orders/details', { id_encomenda: o.ID_Encomenda })
        .then(({ data }) => { if (data?.success) setDetails(data.obj); })
        .catch(() => {})
        .finally(() => setLoadingDetails(false));
    }
  }, [expanded, details, o.ID_Encomenda]);

  const cancel = async (e) => {
    e.stopPropagation();
    const confirmMsg = o.ID_EstadoEncomenda === 'AP'
      ? `Cancelar a encomenda #${o.ID_Encomenda}?`
      : `Emitir nota de crédito para a encomenda concluída #${o.ID_Encomenda}?`;
    if (!window.confirm(confirmMsg)) return;
    setCancelling(true);
    try {
      const endpoint = o.ID_EstadoEncomenda === 'AP' ? '/backoffice/orders/cancel' : '/backoffice/emitirDocumento';
      const body = o.ID_EstadoEncomenda === 'AP'
        ? { id_encomenda: o.ID_Encomenda }
        : { id_encomenda: String(o.ID_Encomenda), tipo_emissao: 'NC' };
      const { data } = await api.post(endpoint, body);
      alert(data?.message || (data?.success ? 'Operação concluída.' : 'Erro.'));
      if (data?.success) onChanged();
    } catch { alert('Erro de comunicação.'); }
    finally { setCancelling(false); }
  };

  const openUrl = (e, url) => { e.stopPropagation(); if (url) window.open(url, '_blank'); };
  const canCancel = o.ID_EstadoEncomenda === 'AP' || ESTADOS_CONCLUIDOS.includes(o.ID_EstadoEncomenda);
  const noneDocs = !o.UrlFatura && !o.UrlRecibo && !o.UrlNotaCredito;

  return (
    <>
      <tr className="hover:bg-[#F9FAFB] cursor-pointer" onClick={onToggle} data-testid={`bo-order-row-${o.ID_Encomenda}`}>
        <td className="px-3 py-2.5 text-[#6B7280]">{expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}</td>
        <td className="px-3 py-2.5 font-medium text-[#111827]">#{o.ID_Encomenda}</td>
        <td className="px-3 py-2.5 text-[#374151]">
          <span className="font-medium">{o.ID_Cliente}</span>
          <span className="text-[#6B7280]"> · {o.NomeCompleto}</span>
        </td>
        <td className="px-3 py-2.5">
          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: estado.bg, color: estado.text }}>
            {o.EstadoEncomenda || estado.label}
          </span>
        </td>
        <td className="px-3 py-2.5 text-[#6B7280]">{fmtDate(o.DataEncomenda)}</td>
        <td className="px-3 py-2.5 text-right font-semibold text-[#111827]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtEUR(o.PrecoFinal)}</td>
        <td className="px-3 py-2.5 text-center">
          {o.UrlFatura && <button onClick={e => openUrl(e, o.UrlFatura)} title="Abrir Fatura" className="p-1 rounded hover:bg-blue-50 text-[#0F1829]"><FileArrowDown size={16} /></button>}
        </td>
        <td className="px-3 py-2.5 text-center">
          {o.UrlRecibo && <button onClick={e => openUrl(e, o.UrlRecibo)} title="Abrir Recibo" className="p-1 rounded hover:bg-green-50 text-green-700"><FileArrowDown size={16} /></button>}
        </td>
        <td className="px-3 py-2.5 text-center">
          {o.UrlNotaCredito && <button onClick={e => openUrl(e, o.UrlNotaCredito)} title="Abrir Nota de Crédito" className="p-1 rounded hover:bg-orange-50 text-orange-700"><FileArrowDown size={16} /></button>}
        </td>
        <td className="px-3 py-2.5 text-center">
          {noneDocs && o.HashEncomenda && (
            <button onClick={e => openUrl(e, `https://my.ihavethepower.net/shop/payment/${o.HashEncomenda}`)} title="Link de Pagamento"
              className="p-1 rounded hover:bg-yellow-50 text-yellow-700"><Receipt size={16} /></button>
          )}
        </td>
        <td className="px-3 py-2.5 text-center">
          {canCancel && (
            <button onClick={cancel} disabled={cancelling}
              title={o.ID_EstadoEncomenda === 'AP' ? 'Cancelar encomenda' : 'Emitir nota de crédito'}
              data-testid={`bo-order-cancel-${o.ID_Encomenda}`}
              className="p-1 rounded hover:bg-red-50 text-red-500 disabled:opacity-40">
              {cancelling ? <SpinnerGap size={16} className="animate-spin" /> : <XCircle size={16} />}
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={11} className="bg-[#F9FAFB] px-4 py-4">
            {loadingDetails && <div className="text-center py-4 text-[#9CA3AF]"><SpinnerGap size={20} className="animate-spin inline mr-2" /> A carregar detalhe...</div>}
            {!loadingDetails && details && <OrderDetail details={details} />}
            {!loadingDetails && !details && <div className="text-center py-4 text-[#9CA3AF]">Sem detalhes disponíveis.</div>}
          </td>
        </tr>
      )}
    </>
  );
}

function OrderDetail({ details }) {
  const d = details?.Detalhes?.[0] || {};
  const items = details?.Itens || [];
  const estados = details?.Estados || [];
  return (
    <div className="space-y-5 text-sm">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Info label="Data Encomenda" value={fmtDate(d.DataEncomenda)} />
        <Info label="Data Pagamento" value={fmtDate(d.DataPagamento)} />
        <Info label="Data Envio" value={fmtDate(d.DataEnvio)} />
        <Info label="Tipo Pagamento" value={d.DescricaoTipoPagamento || d.ID_TipoPagamento || '-'} />
        <Info label="Tipo Transporte" value={d.DescricaoTipoTransporte || '-'} />
        <Info label="Preço Total" value={fmtEUR(d.PrecoTotal)} />
        <Info label="Desconto Total" value={fmtEUR(d.DescontoTotal)} />
        <Info label="IVA Total" value={fmtEUR(d.IvaTotal)} />
        <Info label="Custo Transporte" value={fmtEUR(d.CustoTransporte)} />
        <Info label="Desconto Crédito" value={fmtEUR(d.DescontoCredito)} />
        <Info label="Preço Final" value={<strong className="text-[#0F1829]">{fmtEUR(d.PrecoFinal)}</strong>} />
        <Info label="NetPoints" value={d.NetPointsPessoal ?? '-'} />
      </div>

      {(d.EntidadeMB || d.ReferenciaMB) && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-md px-4 py-2 text-xs text-yellow-900 font-mono">
          MB: Entidade <strong>{d.EntidadeMB}</strong> · Referência <strong>{d.ReferenciaMB}</strong>
        </div>
      )}

      {d.Observacao && (
        <div>
          <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Observações</h4>
          <p className="text-[#374151] text-sm whitespace-pre-wrap">{d.Observacao}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AddressBox title="Morada de Envio" a={{ nome: d.moradaEnvioNome, morada: d.moradaEnvioMorada, cp: d.moradaEnvioCodigoPostal, loc: d.moradaEnvioLocalidade, pais: d.moradaEnvioPais }} />
        <AddressBox title="Morada de Faturação" a={{ nome: d.moradaFaturacaoNome, nif: d.moradaFaturacaoNif, morada: d.moradaFaturacaoMorada, cp: d.moradaFaturacaoCodigoPostal, loc: d.moradaFaturacaoLocalidade, pais: d.moradaFaturacaoPais }} />
      </div>

      {items.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Itens ({items.length})</h4>
          <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#F3F4F6] text-[#6B7280]">
                <tr>
                  <th className="px-3 py-2 text-left">Descrição</th>
                  <th className="px-3 py-2 text-center w-16">Qtd</th>
                  <th className="px-3 py-2 text-right w-28">Preço s/IVA</th>
                  <th className="px-3 py-2 text-right w-28">Preço c/IVA</th>
                  <th className="px-3 py-2 text-right w-20">IVA %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {items.map((i, idx) => (
                  <tr key={`${i.Descricao || 'item'}-${idx}`}>
                    <td className="px-3 py-2 text-[#111827]">{i.Descricao}</td>
                    <td className="px-3 py-2 text-center">{i.Quantidade}</td>
                    <td className="px-3 py-2 text-right">{fmtEUR(i.PrecoUnitarioSemIva)}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmtEUR(i.PrecoUnitarioComIva)}</td>
                    <td className="px-3 py-2 text-right text-[#6B7280]">{i.TaxaIva}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {estados.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Histórico de Estados</h4>
          <div className="bg-white border border-[#E5E7EB] rounded-md divide-y divide-[#E5E7EB]">
            {estados.map((s, idx) => (
              <div key={`${s.EstadoEncomenda || 'est'}-${s.DataMudancaEstado || idx}`} className="px-3 py-2 text-xs flex items-center justify-between">
                <div>
                  <span className="font-medium text-[#111827]">{s.EstadoEncomenda}</span>
                  {s.Observacao && <span className="text-[#6B7280] ml-2">· {s.Observacao}</span>}
                </div>
                <div className="text-[#9CA3AF]">
                  {fmtDate(s.DataMudancaEstado)}
                  {s.EnviadoMail === 'S' && <span className="ml-2 text-green-600">· ✉ email enviado</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <span className="block text-xs text-[#9CA3AF] uppercase tracking-wide">{label}</span>
      <span className="text-[#111827]">{value ?? '-'}</span>
    </div>
  );
}

function AddressBox({ title, a }) {
  const hasData = a.morada || a.nome || a.loc;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md p-3">
      <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">{title}</h4>
      {!hasData ? <p className="text-xs text-[#9CA3AF]">Sem morada.</p> : (
        <div className="text-xs text-[#374151] space-y-0.5">
          {a.nome && <div className="font-medium text-[#111827]">{a.nome}</div>}
          {a.nif && <div>NIF: {a.nif}</div>}
          {a.morada && <div>{a.morada}</div>}
          {(a.cp || a.loc) && <div>{a.cp} {a.loc}</div>}
          {a.pais && <div>{a.pais}</div>}
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════ */
/*  Tab Consultores                                */
/* ══════════════════════════════════════════════ */
function TabConsultores() {
  return (
    <div className="space-y-6">
      <MudarConsultorLead />
      <MudarPercentagemDesconto />
    </div>
  );
}

function MudarConsultorLead() {
  const [idLead, setIdLead] = useState('');
  const [idConsultor, setIdConsultor] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!idLead || !idConsultor) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post('/backoffice/changeLeadConsultant', { id_lead: idLead, id_consultor: idConsultor });
      setResult(data);
      if (data.success) { setIdLead(''); setIdConsultor(''); }
    } catch { setResult({ success: false, message: 'Erro de comunicação.' }); }
    finally { setLoading(false); }
  };

  return (
    <ActionCard title="Mudar Consultor de Lead" testid="section-mudar-consultor-lead">
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <Field label="ID Lead">
          <input data-testid="bo-lead-id" type="text" value={idLead} onChange={e => setIdLead(e.target.value)}
            placeholder="ID da Lead" className={inputCls} />
        </Field>
        <Field label="ID Novo Consultor">
          <input data-testid="bo-lead-consultor" type="text" value={idConsultor} onChange={e => setIdConsultor(e.target.value)}
            placeholder="ID do Consultor" className={inputCls} />
        </Field>
        <ActionBtn onClick={submit} disabled={!idLead || !idConsultor} loading={loading} testid="bo-lead-btn">
          <UserSwitch size={16} />
          Alterar
        </ActionBtn>
      </div>
      <ResultMsg result={result} />
    </ActionCard>
  );
}

function MudarPercentagemDesconto() {
  const [idConsultor, setIdConsultor] = useState('');
  const [tipo, setTipo] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!idConsultor || tipo === '') return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post('/backoffice/changeConsultantDiscount', { id_consultor: idConsultor, tipo_desconto: tipo });
      setResult(data);
      if (data.success) { setIdConsultor(''); setTipo(''); }
    } catch { setResult({ success: false, message: 'Erro de comunicação.' }); }
    finally { setLoading(false); }
  };

  return (
    <ActionCard title="Mudar Percentagem de Desconto de Consultor" testid="section-mudar-desconto">
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <Field label="ID Consultor">
          <input data-testid="bo-desc-id" type="text" value={idConsultor} onChange={e => setIdConsultor(e.target.value)}
            placeholder="ID do Consultor" className={inputCls} />
        </Field>
        <Field label="Tipo de Desconto">
          <select data-testid="bo-desc-select" value={tipo} onChange={e => setTipo(e.target.value)} className={selectCls}>
            <option value="">Selecione desconto...</option>
            <option value="1">Consultor 20%</option>
            <option value="0">Consultor 10%</option>
            <option value="-1">Consultor sem desconto</option>
          </select>
        </Field>
        <ActionBtn onClick={submit} disabled={!idConsultor || tipo === ''} loading={loading} testid="bo-desc-btn">
          <Percent size={16} />
          Alterar
        </ActionBtn>
      </div>
      <ResultMsg result={result} />
    </ActionCard>
  );
}

/* ══════════════════════════════════════════════ */
/*  Tab Empresas                                   */
/* ══════════════════════════════════════════════ */
function TabEmpresas() {
  return (
    <div className="space-y-6">
      <MudarConsultorEmpresa />
    </div>
  );
}

function MudarConsultorEmpresa() {
  const [nif, setNif] = useState('');
  const [idConsultor, setIdConsultor] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const lookupCompany = async () => {
    if (!nif.trim()) { setCompanyName(''); return; }
    try {
      const { data } = await api.post('/backoffice/lookupCompanyByNif', { nif: nif.trim() });
      setCompanyName(data.nome || '');
    } catch { setCompanyName('Erro na pesquisa'); }
  };

  const submit = async () => {
    if (!nif || !idConsultor) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post('/backoffice/changeCompanyConsultant', { nif: nif.trim(), id_consultor: idConsultor });
      setResult(data);
      if (data.success) { setNif(''); setIdConsultor(''); setCompanyName(''); }
    } catch { setResult({ success: false, message: 'Erro de comunicação.' }); }
    finally { setLoading(false); }
  };

  return (
    <ActionCard title="Mudar Consultor de Empresa" testid="section-mudar-consultor-empresa">
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <Field label="NIF da Empresa">
          <input data-testid="bo-emp-nif" type="text" value={nif}
            onChange={e => { setNif(e.target.value); setCompanyName(''); setResult(null); }}
            onBlur={lookupCompany}
            placeholder="NIF da Empresa" className={inputCls} />
        </Field>
        <Field label="ID Novo Consultor">
          <input data-testid="bo-emp-consultor" type="text" value={idConsultor} onChange={e => setIdConsultor(e.target.value)}
            placeholder="ID do Consultor" className={inputCls} />
        </Field>
        <ActionBtn onClick={submit} disabled={!nif || !idConsultor} loading={loading} testid="bo-emp-btn">
          <Buildings size={16} />
          Alterar
        </ActionBtn>
      </div>
      {companyName && (
        <div data-testid="bo-emp-preview" className={`mt-3 px-4 py-2.5 rounded-md border text-sm font-medium ${
          companyName === 'Empresa não encontrada' ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : 'bg-blue-50 border-blue-200 text-blue-700'
        }`}>
          Empresa: <strong>{companyName}</strong>
        </div>
      )}
      <ResultMsg result={result} />
    </ActionCard>
  );
}

/* ══════════════════════════════════════════════ */
/*  Tab Campanhas                                  */
/* ══════════════════════════════════════════════ */
function TabCampanhas() {
  const [campaigns, setCampaigns] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterDesc, setFilterDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [leadTypes, setLeadTypes] = useState([]);
  const [countries, setCountries] = useState([]);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/backoffice/campaigns/list', { campanha: filterName, descricao: filterDesc });
      if (data.success) setCampaigns(data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [filterName, filterDesc]);

  useEffect(() => { loadCampaigns(); }, []);

  useEffect(() => {
    api.get('/backoffice/leadTypes').then(({ data }) => { if (data.success) setLeadTypes(data.data || []); }).catch(() => {});
    api.get('/consultant/countries').then(({ data }) => { if (data.success) setCountries(data.data || []); }).catch(() => {});
  }, []);

  const deleteCampaign = async (id) => {
    if (!window.confirm(`Eliminar a campanha "${id}"?`)) return;
    try {
      const { data } = await api.post('/backoffice/campaigns/delete', { campanha: id });
      if (data.success) loadCampaigns();
      else alert(data.message);
    } catch { alert('Erro ao eliminar.'); }
  };

  return (
    <div className="space-y-6" data-testid="tab-campanhas">
      {/* Create Campaign */}
      <ActionCard title="Criar Campanha" testid="section-criar-campanha">
        <button data-testid="bo-show-create-campaign" onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#001f6e] transition-colors">
          <Plus size={16} /> {showCreate ? 'Fechar Formulário' : 'Nova Campanha'}
        </button>
        {showCreate && <CampaignForm leadTypes={leadTypes} countries={countries}
          onSaved={() => { setShowCreate(false); loadCampaigns(); }} />}
      </ActionCard>

      {/* List Campaigns */}
      <ActionCard title="Ver Campanhas" testid="section-ver-campanhas">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 mb-4">
          <Field label="Nome Campanha">
            <input data-testid="bo-camp-filter-name" type="text" value={filterName} onChange={e => setFilterName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadCampaigns()} placeholder="Nome" className={inputCls} />
          </Field>
          <Field label="Descrição">
            <input data-testid="bo-camp-filter-desc" type="text" value={filterDesc} onChange={e => setFilterDesc(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadCampaigns()} placeholder="Descrição" className={inputCls} />
          </Field>
          <ActionBtn onClick={loadCampaigns} loading={loading} testid="bo-camp-search-btn">Procurar</ActionBtn>
        </div>

        <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="bo-campaigns-table">
            <thead>
              <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-3 py-2 text-left font-medium">Campanha</th>
                <th className="px-3 py-2 text-left font-medium">Descrição</th>
                <th className="px-3 py-2 text-left font-medium hidden md:table-cell">Moosend</th>
                <th className="px-3 py-2 text-left font-medium hidden md:table-cell">Facebook</th>
                <th className="px-3 py-2 text-center font-medium w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {campaigns.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#9CA3AF]">
                  {loading ? 'A carregar...' : 'Sem campanhas.'}
                </td></tr>
              )}
              {campaigns.map(c => (
                <CampaignRow key={c.Campanha} campaign={c}
                  expanded={expandedId === c.Campanha}
                  onToggle={() => { setExpandedId(expandedId === c.Campanha ? null : c.Campanha); setEditData(null); }}
                  onDelete={() => deleteCampaign(c.Campanha)}
                  onEdit={() => setEditData(editData === c.Campanha ? null : c.Campanha)}
                  editMode={editData === c.Campanha}
                  leadTypes={leadTypes} countries={countries}
                  onSaved={() => { setEditData(null); loadCampaigns(); }}
                />
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#9CA3AF] mt-2">{campaigns.length} campanha(s)</p>
      </ActionCard>
    </div>
  );
}

function CampaignTestEmailButton({ campanha }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setSending(true); setResult(null);
    try {
      const { data } = await api.post('/backoffice/campaigns/test-email', {
        campanha, email_destino: email.trim(),
      });
      setResult(data);
      if (data.success) setTimeout(() => { setOpen(false); setResult(null); setEmail(''); }, 2500);
    } catch (e) {
      setResult({ success: false, message: e?.response?.data?.message || 'Erro de comunicação.' });
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`test-email-btn-${campanha}`}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] text-[#0F1829] text-xs font-medium rounded-md hover:bg-[#F9FAFB] transition-colors"
      >
        <EnvelopeSimple size={14} /> Email teste
      </button>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 flex-wrap" data-testid={`test-email-form-${campanha}`}>
      <input
        type="email"
        value={email}
        autoFocus
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && email.trim()) send(); }}
        placeholder="email@exemplo.com"
        data-testid={`test-email-input-${campanha}`}
        className="px-3 py-1.5 border border-[#E5E7EB] rounded-md text-xs w-56 focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none"
      />
      <button
        type="button"
        onClick={send}
        disabled={sending || !email.trim()}
        data-testid={`test-email-send-${campanha}`}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0F1829] text-white text-xs font-medium rounded-md hover:bg-[#001f6e] transition-colors disabled:opacity-60"
      >
        {sending ? <SpinnerGap size={14} className="animate-spin" /> : <EnvelopeSimple size={14} />}
        {sending ? 'A enviar…' : 'Enviar'}
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setResult(null); setEmail(''); }}
        disabled={sending}
        className="px-2 py-1.5 text-xs text-[#6B7280] hover:text-[#0F1829]"
      >
        Cancelar
      </button>
      {result && (
        <span
          data-testid={`test-email-result-${campanha}`}
          className={`flex items-center gap-1 text-xs ${result.success ? 'text-[#065F46]' : 'text-[#991B1B]'}`}
        >
          {result.success ? <CheckCircle size={14} /> : <XCircle size={14} />}
          {result.message}
        </span>
      )}
    </div>
  );
}

function CampaignRow({ campaign: c, expanded, onToggle, onDelete, onEdit, editMode, leadTypes, countries, onSaved }) {
  return (
    <>
      <tr className="hover:bg-[#F9FAFB] cursor-pointer" onClick={onToggle} data-testid={`campaign-row-${c.Campanha}`}>
        <td className="px-3 py-2 font-medium text-[#111827]">{c.Campanha}</td>
        <td className="px-3 py-2 text-[#374151]">{c.Descricao}</td>
        <td className="px-3 py-2 text-[#6B7280] hidden md:table-cell">{c.AdMoosend || '-'}</td>
        <td className="px-3 py-2 text-[#6B7280] hidden md:table-cell">{c.AdFacebook || '-'}</td>
        <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 text-[#9CA3AF] hover:text-red-500 transition-colors" title="Eliminar">
            <Trash size={16} />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} className="bg-[#F9FAFB] px-4 py-4">
            {!editMode ? (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                  <div><span className="block text-xs text-[#9CA3AF]">Tipo Lead</span><span className="font-medium">{c.TipoLead || '-'}</span></div>
                  <div><span className="block text-xs text-[#9CA3AF]">País Default</span><span className="font-medium">{c.PaisDefault || '-'}</span></div>
                  <div><span className="block text-xs text-[#9CA3AF]">ID Moosend</span><span className="font-medium">{c.IdMoosend || '-'}</span></div>
                  <div><span className="block text-xs text-[#9CA3AF]">ID Facebook</span><span className="font-medium">{c.IdFacebook || '-'}</span></div>
                </div>
                {c.MensagemEmailLead && (
                  <div className="mb-3">
                    <span className="block text-xs text-[#9CA3AF] mb-1.5">Corpo do Email</span>
                    <div className="bg-white border border-[#E5E7EB] rounded-md px-4 py-3 text-sm text-[#374151] max-h-64 overflow-auto prose prose-sm max-w-none prose-a:text-[#0F1829] prose-p:my-2"
                      data-testid={`campaign-email-body-${c.Campanha}`}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.MensagemEmailLead, { USE_PROFILES: { html: true } }) }} />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button onClick={e => { e.stopPropagation(); onEdit(); }} data-testid={`edit-campaign-${c.Campanha}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0F1829] text-white text-xs font-medium rounded-md hover:bg-[#001f6e] transition-colors">
                    <PencilSimple size={14} /> Editar
                  </button>
                  {c.MensagemEmailLead && (
                    <CampaignTestEmailButton campanha={c.Campanha} />
                  )}
                </div>
              </div>
            ) : (
              <CampaignForm editCampaign={c} leadTypes={leadTypes} countries={countries} onSaved={onSaved} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function CampaignForm({ editCampaign: ec, leadTypes, countries, onSaved }) {
  const isEdit = !!ec;
  const [form, setForm] = useState({
    campanha: ec?.Campanha || '', descricao: ec?.Descricao || '',
    tipo_lead: ec?.TipoLead || '', pais: ec?.PaisDefault || 'PRT',
    moosend_id: ec?.IdMoosend || '', moosend_nome: ec?.AdMoosend || '',
    facebook_id: ec?.IdFacebook || '', facebook_nome: ec?.AdFacebook || '',
    email_message: ec?.MensagemEmailLead || ''
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true); setResult(null);
    const endpoint = isEdit ? '/backoffice/campaigns/edit' : '/backoffice/campaigns/create';
    try {
      const { data } = await api.post(endpoint, form);
      setResult(data);
      if (data.success) { if (onSaved) onSaved(); }
    } catch (e) {
      const st = e?.response?.status;
      const msg = e?.response?.data?.message || e?.response?.data?.detail;
      const humanized =
        st === 401 ? 'Sessão expirou. Faça login outra vez e volte a tentar.'
        : st === 413 ? 'O corpo do email é demasiado grande. Reduza o conteúdo e tente novamente.'
        : st === 504 || st === 502 ? 'Servidor demorou a responder (gateway timeout). A alteração pode ter sido gravada — recarregue e verifique.'
        : msg || (st ? `Erro do servidor (HTTP ${st}).` : 'Erro de comunicação.');
      setResult({ success: false, message: humanized });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-3 space-y-3" data-testid={isEdit ? 'edit-campaign-form' : 'create-campaign-form'}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">ID Campanha</label>
          <input data-testid="camp-id" type="text" value={form.campanha} onChange={e => set('campanha', e.target.value)}
            disabled={isEdit} placeholder="ID Campanha" className={`${inputCls} ${isEdit ? 'bg-[#F9FAFB] text-[#6B7280]' : ''}`} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Descrição</label>
          <input data-testid="camp-desc" type="text" value={form.descricao} onChange={e => set('descricao', e.target.value)}
            placeholder="Descrição Campanha" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Tipo Lead</label>
          <select data-testid="camp-tipo" value={form.tipo_lead} onChange={e => set('tipo_lead', e.target.value)} className={selectCls}>
            <option value="">Selecione...</option>
            {leadTypes.map(t => <option key={t.Id} value={t.Id}>{t.Descricao} ({t.Id})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">País Default</label>
          <select data-testid="camp-pais" value={form.pais} onChange={e => set('pais', e.target.value)} className={selectCls}>
            {countries.map(c => <option key={c.ID_Pais} value={c.ID_Pais}>{c.Descricao}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-orange-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-orange-600 mb-2">Anúncio Moosend</p>
          <input data-testid="camp-moosend-id" type="text" value={form.moosend_id} onChange={e => set('moosend_id', e.target.value)}
            placeholder="ID Mailing List" className={`${inputCls} mb-2`} />
          <input data-testid="camp-moosend-nome" type="text" value={form.moosend_nome} onChange={e => set('moosend_nome', e.target.value)}
            placeholder="Nome Mailing List" className={inputCls} />
        </div>
        <div className="border border-blue-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-blue-600 mb-2">Anúncio Facebook</p>
          <input data-testid="camp-facebook-id" type="text" value={form.facebook_id} onChange={e => set('facebook_id', e.target.value)}
            placeholder="ID Anúncio" className={`${inputCls} mb-2`} />
          <input data-testid="camp-facebook-nome" type="text" value={form.facebook_nome} onChange={e => set('facebook_nome', e.target.value)}
            placeholder="Nome Anúncio" className={inputCls} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-[#6B7280] mb-1">Corpo do Email da Campanha</label>
        <textarea data-testid="camp-email" value={form.email_message} onChange={e => set('email_message', e.target.value)}
          rows={4} placeholder="Mensagem de email HTML..." className={`${inputCls} resize-y`} />
      </div>
      <div className="flex items-center gap-3">
        <ActionBtn onClick={submit} loading={saving} disabled={!form.campanha || !form.descricao} testid="camp-submit-btn">
          {isEdit ? 'Guardar Alterações' : 'Criar Campanha'}
        </ActionBtn>
        {result && <ResultMsg result={result} />}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════ */
/*  Tab Subscrição Leads                           */
/* ══════════════════════════════════════════════ */
function TabSubscricaoLeads() {
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.post('/backoffice/campaigns/list', { campanha: '', descricao: '' })
      .then(({ data }) => { if (data.success) setCampaigns(data.data || []); }).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="tab-subscricao">
      <SubscreverConsultor campaigns={campaigns} />
      <ProcurarSubscricoes campaigns={campaigns} />
    </div>
  );
}

function SubscreverConsultor({ campaigns }) {
  const [idConsultor, setIdConsultor] = useState('');
  const [consultantName, setConsultantName] = useState('');
  const [campanha, setCampanha] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const lookupConsultant = async () => {
    if (!idConsultor.trim()) { setConsultantName(''); return; }
    try {
      const { data } = await api.post('/backoffice/consultantLookup', { id_consultor: idConsultor });
      setConsultantName(data.nome || '');
    } catch { setConsultantName('Erro'); }
  };

  const submit = async () => {
    if (!idConsultor || !campanha || !dataInicio || !dataFim) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post('/backoffice/subscriptions/create', {
        id_consultor: idConsultor, id_campanha: campanha, data_inicio: dataInicio, data_fim: dataFim
      });
      setResult(data);
      if (data.success) { setIdConsultor(''); setCampanha(''); setDataInicio(''); setDataFim(''); setConsultantName(''); }
    } catch { setResult({ success: false, message: 'Erro de comunicação.' }); }
    finally { setLoading(false); }
  };

  return (
    <ActionCard title="Subscrever Consultor para Receber Leads" testid="section-subscrever-consultor">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">ID Consultor</label>
          <input data-testid="sub-consultor-id" type="text" value={idConsultor}
            onChange={e => { setIdConsultor(e.target.value); setConsultantName(''); }}
            onBlur={lookupConsultant} placeholder="ID Consultor" className={inputCls} />
          {consultantName && (
            <p data-testid="sub-consultor-name" className={`text-xs mt-1 font-medium ${consultantName === 'Consultor não encontrado' ? 'text-red-500' : 'text-green-600'}`}>
              {consultantName}
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Campanha</label>
          <select data-testid="sub-campanha" value={campanha} onChange={e => setCampanha(e.target.value)} className={selectCls}>
            <option value="">Selecione campanha...</option>
            {campaigns.map(c => <option key={c.Campanha} value={c.Campanha}>{c.Descricao}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Data Início</label>
          <input data-testid="sub-data-inicio" type="datetime-local" value={dataInicio}
            onChange={e => setDataInicio(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Data Fim</label>
          <input data-testid="sub-data-fim" type="datetime-local" value={dataFim}
            onChange={e => setDataFim(e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="mt-3">
        <ActionBtn onClick={submit} disabled={!idConsultor || !campanha || !dataInicio || !dataFim}
          loading={loading} testid="sub-criar-btn">Subscrever</ActionBtn>
      </div>
      <ResultMsg result={result} />
    </ActionCard>
  );
}

function ProcurarSubscricoes({ campaigns }) {
  const [subs, setSubs] = useState([]);
  const [filterConsultor, setFilterConsultor] = useState('');
  const [filterCampanha, setFilterCampanha] = useState('');
  const [filterInicio, setFilterInicio] = useState(() => { const d = new Date(); return d.toISOString().slice(0,10); });
  const [filterFim, setFilterFim] = useState(() => { const d = new Date(); d.setDate(d.getDate()+7); return d.toISOString().slice(0,10); });
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const loadSubs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/backoffice/subscriptions/list', {
        id_consultor: filterConsultor, id_campanha: filterCampanha,
        data_inicio: filterInicio, data_fim: filterFim
      });
      if (data.success) setSubs(data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [filterConsultor, filterCampanha, filterInicio, filterFim]);

  useEffect(() => { loadSubs(); }, []);

  const deleteSub = async (id) => {
    if (!window.confirm('Eliminar esta subscrição?')) return;
    try {
      const { data } = await api.post('/backoffice/subscriptions/delete', { id_subscricao: id });
      if (data.success) loadSubs();
      else alert(data.message);
    } catch { alert('Erro ao eliminar.'); }
  };

  const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('pt-PT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }); } catch { return d; } };

  return (
    <ActionCard title="Procurar Subscrições" testid="section-procurar-subscricoes">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <Field label="ID Consultor">
          <input data-testid="sub-filter-consultor" type="text" value={filterConsultor}
            onChange={e => setFilterConsultor(e.target.value)} placeholder="ID" className={inputCls} />
        </Field>
        <Field label="Campanha">
          <select data-testid="sub-filter-campanha" value={filterCampanha} onChange={e => setFilterCampanha(e.target.value)} className={selectCls}>
            <option value="">Todas</option>
            {campaigns.map(c => <option key={c.Campanha} value={c.Campanha}>{c.Descricao}</option>)}
          </select>
        </Field>
        <Field label="Data Início">
          <input data-testid="sub-filter-inicio" type="date" value={filterInicio}
            onChange={e => setFilterInicio(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Data Fim">
          <input data-testid="sub-filter-fim" type="date" value={filterFim}
            onChange={e => setFilterFim(e.target.value)} className={inputCls} />
        </Field>
        <div className="flex items-end">
          <ActionBtn onClick={loadSubs} loading={loading} testid="sub-pesquisar-btn">Pesquisar</ActionBtn>
        </div>
      </div>

      <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="sub-table">
          <thead>
            <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
              <th className="px-3 py-2 text-left font-medium">Campanha</th>
              <th className="px-3 py-2 text-left font-medium">Consultor</th>
              <th className="px-3 py-2 text-left font-medium hidden md:table-cell">Data Início</th>
              <th className="px-3 py-2 text-left font-medium hidden md:table-cell">Data Fim</th>
              <th className="px-3 py-2 text-center font-medium w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {subs.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#9CA3AF]">
                {loading ? 'A carregar...' : 'Sem subscrições encontradas.'}
              </td></tr>
            )}
            {subs.map(s => (
              <SubRow key={s.Id} sub={s} fmtDate={fmtDate}
                expanded={expandedId === s.Id}
                onToggle={() => setExpandedId(expandedId === s.Id ? null : s.Id)}
                onDelete={() => deleteSub(s.Id)} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#9CA3AF] mt-2">{subs.length} subscrição(ões)</p>
    </ActionCard>
  );
}

function SubRow({ sub: s, fmtDate, expanded, onToggle, onDelete }) {
  return (
    <>
      <tr className="hover:bg-[#F9FAFB] cursor-pointer" onClick={onToggle} data-testid={`sub-row-${s.Id}`}>
        <td className="px-3 py-2 font-medium text-[#111827]">{s.NomeCampanha}</td>
        <td className="px-3 py-2 text-[#374151]">{s.NomeConsultor}</td>
        <td className="px-3 py-2 text-[#6B7280] hidden md:table-cell">{fmtDate(s.DataInicio)}</td>
        <td className="px-3 py-2 text-[#6B7280] hidden md:table-cell">{fmtDate(s.DataFim)}</td>
        <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 text-[#9CA3AF] hover:text-red-500 transition-colors" title="Eliminar">
            <Trash size={16} />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} className="bg-[#F9FAFB] px-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><span className="block text-xs text-[#9CA3AF]">ID Campanha</span><span className="font-medium">{s.NomeCampanha}</span></div>
              <div><span className="block text-xs text-[#9CA3AF]">Descrição</span><span className="font-medium">{s.Descricao}</span></div>
              <div><span className="block text-xs text-[#9CA3AF]">ID Consultor</span><span className="font-medium">{s.IdCliente}</span></div>
              <div><span className="block text-xs text-[#9CA3AF]">Nome Consultor</span><span className="font-medium">{s.NomeConsultor}</span></div>
              <div><span className="block text-xs text-[#9CA3AF]">Data Início</span><span className="font-medium">{fmtDate(s.DataInicio)}</span></div>
              <div><span className="block text-xs text-[#9CA3AF]">Data Fim</span><span className="font-medium">{fmtDate(s.DataFim)}</span></div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════ */
/*  Main Page                                      */
/* ══════════════════════════════════════════════ */
function TabWhatsAppTemplates() {
  const [templates, setTemplates] = useState([]);
  const [estados, setEstados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, tipo_lead, estado, mensagem}
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const [tplRes, estRes] = await Promise.all([
        api.get('/backoffice/whatsapp-templates'),
        api.get('/backoffice/whatsapp-templates/estados'),
      ]);
      setTemplates(tplRes?.data?.items || []);
      setEstados(estRes?.data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erro a carregar templates.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    setSaving(true); setErr('');
    try {
      await api.post('/backoffice/whatsapp-templates', {
        tipo_lead: editing.tipo_lead,
        estado: editing.estado || null,
        mensagem: editing.mensagem,
      });
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erro ao guardar.');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminar este template?')) return;
    try {
      await api.delete(`/backoffice/whatsapp-templates/${id}`);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Erro ao eliminar.'); }
  };

  const tipoLabel = { CS: 'Consultor (CS)', BC: 'B2C', BB: 'B2B' };

  return (
    <div className="space-y-6" data-testid="tab-whatsapp-templates">
      <ActionCard title="WhatsApp Templates por Tipo de Lead e Estado" testid="card-whatsapp-templates">
        <p className="text-sm text-[#6B7280] mb-4">
          Define a mensagem sugerida quando os consultores clicam no ícone WhatsApp em qualquer lead.
          Pode usar placeholders: <code>{'{Nome}'}</code>, <code>{'{NomeCompleto}'}</code>, <code>{'{Empresa}'}</code>, <code>{'{Estado}'}</code>, <code>{'{Telemovel}'}</code>, <code>{'{Email}'}</code>.
          O lookup é hierárquico: primeiro tenta <em>(tipo, estado)</em> exacto, depois <em>(tipo, *)</em> default.
        </p>

        {err && <div className="mb-3 p-3 bg-[#FEF2F2] border border-[#FECACA] rounded text-sm text-[#991B1B]">{err}</div>}

        <button
          onClick={() => setEditing({ id: null, tipo_lead: 'CS', estado: '', mensagem: '' })}
          data-testid="add-whatsapp-template-btn"
          className="flex items-center gap-2 px-3 py-1.5 bg-[#0F1829] text-white text-xs font-medium rounded hover:bg-[#001f6e] transition mb-4"
        >
          <Plus size={14} /> Novo template
        </button>

        {loading ? (
          <div className="p-6 text-center text-sm text-[#6B7280]">A carregar…</div>
        ) : templates.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#6B7280]">Sem templates. Cria o primeiro.</div>
        ) : (
          <div className="space-y-2">
            {templates.map(t => (
              <div key={t.ID_WhatsAppTemplate} data-testid={`whatsapp-template-${t.ID_WhatsAppTemplate}`}
                   className="border border-[#E5E7EB] rounded-md p-3 bg-white hover:bg-[#F9FAFB]">
                <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-[#EEF2FF] text-[#0F1829] text-xs rounded font-mono">{tipoLabel[t.TipoLead] || t.TipoLead}</span>
                    {t.ID_EstadoNegocio ? (
                      <span className="px-2 py-0.5 bg-[#F0FDF4] text-[#065F46] text-xs rounded">
                        {t.EstadoDescricao || t.ID_EstadoNegocio}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-[#FFFBEB] text-[#92400E] text-xs rounded">DEFAULT</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditing({ id: t.ID_WhatsAppTemplate, tipo_lead: t.TipoLead, estado: t.ID_EstadoNegocio || '', mensagem: t.Mensagem })}
                      data-testid={`edit-whatsapp-template-${t.ID_WhatsAppTemplate}`}
                      className="p-1.5 text-[#0F1829] hover:bg-[#F3F4F6] rounded">
                      <PencilSimple size={14} />
                    </button>
                    <button
                      onClick={() => remove(t.ID_WhatsAppTemplate)}
                      data-testid={`delete-whatsapp-template-${t.ID_WhatsAppTemplate}`}
                      className="p-1.5 text-[#991B1B] hover:bg-[#FEF2F2] rounded">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-[#374151] whitespace-pre-wrap">{t.Mensagem}</p>
              </div>
            ))}
          </div>
        )}
      </ActionCard>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6" onClick={e => e.stopPropagation()} data-testid="whatsapp-template-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editing.id ? 'Editar template' : 'Novo template'}</h3>
              <button onClick={() => setEditing(null)} className="text-[#6B7280] hover:text-[#0F1829]"><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Tipo de Lead</label>
                  <select
                    value={editing.tipo_lead}
                    onChange={e => setEditing({ ...editing, tipo_lead: e.target.value, estado: '' })}
                    data-testid="whatsapp-template-tipo"
                    disabled={!!editing.id}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm">
                    <option value="CS">Consultor (CS)</option>
                    <option value="BC">B2C</option>
                    <option value="BB">B2B</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Estado da Lead</label>
                  <select
                    value={editing.estado}
                    onChange={e => setEditing({ ...editing, estado: e.target.value })}
                    data-testid="whatsapp-template-estado"
                    disabled={!!editing.id}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm">
                    <option value="">— DEFAULT (qualquer estado) —</option>
                    {estados.filter(e => e.TipoLead === editing.tipo_lead).map(e => (
                      <option key={e.ID_EstadoNegocio} value={e.ID_EstadoNegocio}>
                        {e.ID_EstadoNegocio} — {e.Descricao}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">
                  Mensagem (placeholders: {'{Nome}'}, {'{Empresa}'}, {'{Estado}'}, {'{Telemovel}'}, {'{Email}'})
                </label>
                <textarea
                  rows={6}
                  value={editing.mensagem}
                  onChange={e => setEditing({ ...editing, mensagem: e.target.value })}
                  data-testid="whatsapp-template-mensagem"
                  placeholder="Olá {Nome}, sou consultor IHTP e..."
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm font-mono leading-relaxed" />
                <p className="text-xs text-[#9CA3AF] mt-1">{editing.mensagem.length}/2000 caracteres</p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setEditing(null)} disabled={saving}
                        className="px-3 py-1.5 text-sm text-[#6B7280] hover:text-[#0F1829]">Cancelar</button>
                <button onClick={save} disabled={saving || !editing.mensagem.trim()}
                        data-testid="whatsapp-template-save"
                        className="px-4 py-1.5 bg-[#0F1829] text-white text-sm font-medium rounded hover:bg-[#001f6e] transition disabled:opacity-60 flex items-center gap-2">
                  {saving ? <SpinnerGap size={14} className="animate-spin" /> : <FloppyDisk size={14} />}
                  {saving ? 'A guardar…' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BackofficePage() {
  const [activeTab, setActiveTab] = useState('geral');

  return (
    <div data-testid="backoffice-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">Ferramentas BackOffice</h1>
        <p className="text-sm text-[#6B7280] mt-1">Ferramentas de administração (acesso restrito)</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 overflow-x-auto -mx-3 sm:mx-0">
        <div className="inline-flex gap-1 bg-[#F3F4F6] rounded-lg p-1 mx-3 sm:mx-0 whitespace-nowrap" data-testid="bo-tabs">
          {tabs.map(t => (
            <button key={t.id} data-testid={`bo-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-all flex-shrink-0 ${
                activeTab === t.id
                  ? 'bg-white text-[#0F1829] shadow-sm'
                  : 'text-[#6B7280] hover:text-[#111827]'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'geral' && <TabGeral />}
      {activeTab === 'encomendas' && <TabEncomendas />}
      {activeTab === 'consultores' && <TabConsultores />}
      {activeTab === 'empresas' && <TabEmpresas />}
      {activeTab === 'campanhas' && <TabCampanhas />}
      {activeTab === 'subscricao' && <TabSubscricaoLeads />}
      {activeTab === 'carregamentos' && <TabCarregamentos />}
      {activeTab === 'fbleads' && <TabFacebookLeads />}
      {activeTab === 'webhookrej' && <TabWebhookRejeicoes />}
      {activeTab === 'whatsapp' && <TabWhatsAppTemplates />}
    </div>
  );
}
